<template>
	<BaseField
		:label="label"
		:status="status"
		:error="error"
		:direction="direction"
		:fit="fit"
		:hide-meta="hideMeta || !label"
	>
		<template v-if="$slots['label-after']" #label-after>
			<slot name="label-after" />
		</template>
		<label :class="switchClasses">
			<input
				:id="id ?? undefined"
				:class="bemm('input')"
				type="checkbox"
				:checked="modelValue"
				:disabled="disabled"
				@change="toggle"
			/>
			<span :class="bemm('track')">
				<span :class="bemm('icon')">
					<Icon :name="modelValue ? Icons.CHECK : Icons.CROSS" />
				</span>
				<span :class="bemm('thumb')"></span>
			</span>
		</label>
	</BaseField>
</template>

<script lang="ts" setup>
import { computed, toRefs } from 'vue';
import { useBemm } from 'bemm';
import BaseField from './BaseField.vue';
import { Icon, Icons } from '../Icon';

defineOptions({
	name: 'ArSwitch',
	inheritAttrs: false,
});

const props = withDefaults(
	defineProps<{
		modelValue?: boolean;
		label?: string;
		status?: string;
		error?: string;
		disabled?: boolean;
		id?: string | null;
		direction?: 'auto' | 'row' | 'column';
		/** Shrink-wrap the field instead of filling its container, for use in
		 * dense layouts such as table cells. */
		fit?: boolean;
		hideMeta?: boolean;
	}>(),
	{
		modelValue: false,
		label: '',
		status: '',
		error: '',
		disabled: false,
		id: null,
		direction: 'auto',
		fit: false,
		hideMeta: false,
	}
);
const { modelValue, label, status, error, disabled, id } = toRefs(props);

const emit = defineEmits<{
	(e: 'update:modelValue', value: boolean): void;
	(e: 'change', value: boolean): void;
}>();

const bemm = useBemm('ar-switch', {
	return: 'string',
	includeBaseClass: true,
});

const toggle = (event: Event) => {
	const target = event.target as HTMLInputElement;
	emit('update:modelValue', target.checked);
	emit('change', target.checked);
};

const switchClasses = computed(() =>
	bemm('', [
		props.modelValue ? 'checked' : '',
		props.disabled ? 'disabled' : '',
	])
);
</script>

<style lang="scss">
.ar-switch {
	$b: &;
	--int-switch-padding: calc(var(--space-xs) * 0.5);
	--int-switch-thumb-size: calc(var(--space) * 1.5);
	--int-switch-height: calc(
		(var(--int-switch-padding) * 2) + var(--int-switch-thumb-size)
	);
	--int-switch-width: calc(
		(var(--int-switch-thumb-size) * 2) + (var(--int-switch-padding) * 2)
	);
	--int-switch-track-off: color-mix(
		in srgb,
		var(--color-foreground),
		transparent 80%
	);
	--int-switch-track-on: var(--color-foreground);
	--int-switch-icon-color: var(--color-background);
	--int-switch-thumb-color: var(--color-background);

	position: relative;
	display: inline-flex;
	width: var(--int-switch-width);
	height: var(--int-switch-height);
	cursor: pointer;

	&__input {
		position: absolute;
		width: 1px;
		height: 1px;
		padding: 0;
		margin: -1px;
		overflow: hidden;
		clip: rect(0, 0, 0, 0);
		border: 0;
	}

	&__track {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: var(--int-switch-track-off);
		transition:
			background-color 0.2s ease,
			box-shadow 0.2s ease,
			transform 0.18s ease;
		border-radius: var(--int-switch-height);
		display: flex;
		align-items: center;
		justify-content: center;
		padding: var(--int-switch-padding);
		box-shadow: inset 0 0 0 1px
			color-mix(in srgb, var(--color-foreground), transparent 75%);
	}

	&__icon {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: flex-end;
		padding-inline: calc(var(--space-s) * 1.25);
		color: var(--int-switch-icon-color);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-extra-bold);
		line-height: 1;
		user-select: none;
		pointer-events: none;
		transition:
			transform 0.24s ease,
			opacity 0.2s ease;
	}

	&__thumb {
		position: absolute;
		left: var(--int-switch-padding);
		top: var(--int-switch-padding);
		height: var(--int-switch-thumb-size);
		width: var(--int-switch-thumb-size);
		background-color: var(--int-switch-thumb-color);
		transition:
			transform 0.42s cubic-bezier(0.2, 1.3, 0.35, 1),
			background-color 0.2s ease;
		border-radius: 50%;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
	}

	&:not(#{$b}--disabled):hover {
		#{$b}__track {
			transform: translateY(-1px);
			box-shadow:
				inset 0 0 0 1px
					color-mix(in srgb, var(--color-foreground), transparent 75%),
				0 3px 8px color-mix(in srgb, var(--color-primary), transparent 75%);
		}

		#{$b}__icon {
			transform: scale(1.06);
		}

		#{$b}__thumb {
			box-shadow: 0 3px 10px rgba(0, 0, 0, 0.22);
		}
	}

	&--checked {
		#{$b}__icon {
			justify-content: flex-start;
		}

		#{$b}__track {
			background-color: var(--int-switch-track-on);
		}

		#{$b}__thumb {
			transform: translateX(
				calc(
					var(--int-switch-width) - var(--int-switch-thumb-size) -
						(var(--int-switch-padding) * 2)
				)
			);
		}
	}

	&--disabled {
		opacity: 0.6;
		cursor: not-allowed;

		#{$b}__track,
		#{$b}__icon,
		#{$b}__thumb {
			transition: none;
		}
	}
}
</style>
